import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  approvePurchaseOrder,
  getPurchaseOrderById,
  listPurchaseOrders,
  listStores,
  listSuppliers,
  savePurchaseOrder,
} from "../../services/purchaseOrderService";

const getDefaultFilters = () => {
  const today = new Date();
  const from = new Date(today);
  from.setDate(from.getDate() - 7);
  return {
    toStore: null,
    supplier: null,
    fromDate: from.toISOString().split("T")[0],
    toDate: today.toISOString().split("T")[0],
    cancelledPo: false,
  };
};

const initialState = {
  filters: getDefaultFilters(),
  stores: [],
  suppliers: [],
  purchaseOrders: [],
  selectedPo: null,
  loading: false,
  detailLoading: false,
  saving: false,
  approving: false,
  lookupsLoading: false,
  error: null,
};

export const fetchPurchaseLookups = createAsyncThunk(
  "purchaseOrder/fetchPurchaseLookups",
  async (_, { rejectWithValue }) => {
    try {
      const [stores, suppliers] = await Promise.all([
        listStores(),
        listSuppliers(),
      ]);
      return { stores, suppliers };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const searchPurchaseSuppliers = createAsyncThunk(
  "purchaseOrder/searchPurchaseSuppliers",
  async (query, { rejectWithValue }) => {
    try {
      return await listSuppliers(query);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPurchaseOrders = createAsyncThunk(
  "purchaseOrder/fetchPurchaseOrders",
  async (filters, { rejectWithValue }) => {
    try {
      return await listPurchaseOrders(filters || getDefaultFilters());
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPurchaseOrderDetail = createAsyncThunk(
  "purchaseOrder/fetchPurchaseOrderDetail",
  async (purchaseId, { rejectWithValue }) => {
    try {
      return await getPurchaseOrderById(purchaseId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const savePurchaseOrderItems = createAsyncThunk(
  "purchaseOrder/savePurchaseOrderItems",
  async ({ purchaseId, items, userId }, { rejectWithValue }) => {
    try {
      await savePurchaseOrder(purchaseId, items, userId);
      return await getPurchaseOrderById(purchaseId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const approvePurchaseOrderItems = createAsyncThunk(
  "purchaseOrder/approvePurchaseOrderItems",
  async ({ purchaseId, items, userId }, { rejectWithValue }) => {
    try {
      await approvePurchaseOrder(purchaseId, items, userId);
      return await getPurchaseOrderById(purchaseId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const purchaseOrderSlice = createSlice({
  name: "purchaseOrder",
  initialState,
  reducers: {
    setPurchaseFilters: (state, action) => {
      state.filters = action.payload || getDefaultFilters();
    },
    clearSelectedPurchaseOrder: (state) => {
      state.selectedPo = null;
    },
    clearPurchaseOrderError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPurchaseLookups.pending, (state) => {
        state.lookupsLoading = true;
      })
      .addCase(fetchPurchaseLookups.fulfilled, (state, action) => {
        state.lookupsLoading = false;
        state.stores = action.payload?.stores || [];
        state.suppliers = action.payload?.suppliers || [];
      })
      .addCase(fetchPurchaseLookups.rejected, (state, action) => {
        state.lookupsLoading = false;
        state.error = action.payload || "Failed to load lookups";
      })
      .addCase(searchPurchaseSuppliers.fulfilled, (state, action) => {
        state.suppliers = action.payload || [];
      })
      .addCase(fetchPurchaseOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPurchaseOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.purchaseOrders = action.payload || [];
      })
      .addCase(fetchPurchaseOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load purchase orders";
        state.purchaseOrders = [];
      })
      .addCase(fetchPurchaseOrderDetail.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(fetchPurchaseOrderDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selectedPo = action.payload;
      })
      .addCase(fetchPurchaseOrderDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.payload || "Failed to load purchase order";
        state.selectedPo = null;
      })
      .addCase(savePurchaseOrderItems.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(savePurchaseOrderItems.fulfilled, (state, action) => {
        state.saving = false;
        state.selectedPo = action.payload;
        const id = action.payload?.poId;
        if (id) {
          state.purchaseOrders = state.purchaseOrders.map((po) =>
            po.poId === id
              ? {
                  ...po,
                  totalAmt: action.payload.totalAmt,
                  discAmt: action.payload.discAmt,
                  gstAmt: action.payload.gstAmt,
                  netAmt: action.payload.netAmt,
                  verify: action.payload.verify,
                }
              : po
          );
        }
      })
      .addCase(savePurchaseOrderItems.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || "Failed to save purchase order";
      })
      .addCase(approvePurchaseOrderItems.pending, (state) => {
        state.approving = true;
        state.error = null;
      })
      .addCase(approvePurchaseOrderItems.fulfilled, (state, action) => {
        state.approving = false;
        state.selectedPo = action.payload;
        const id = action.payload?.poId;
        if (id) {
          state.purchaseOrders = state.purchaseOrders.map((po) =>
            po.poId === id
              ? {
                  ...po,
                  totalAmt: action.payload.totalAmt,
                  discAmt: action.payload.discAmt,
                  gstAmt: action.payload.gstAmt,
                  netAmt: action.payload.netAmt,
                  verify: "Close",
                }
              : po
          );
        }
      })
      .addCase(approvePurchaseOrderItems.rejected, (state, action) => {
        state.approving = false;
        state.error = action.payload || "Failed to approve purchase order";
      });
  },
});

export const {
  setPurchaseFilters,
  clearSelectedPurchaseOrder,
  clearPurchaseOrderError,
} = purchaseOrderSlice.actions;

export { getDefaultFilters };
export default purchaseOrderSlice.reducer;
